import Vue from 'vue';
import types from '../../mutation-types';
import getters from './getters';
import actions from './actions';
import {
  TryVueSet,
  findPendingMessageIndex,
  MAX_SUBSCRIBED_CONVERSATIONS,
} from './helpers';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

// Maximum number of recent conversations that retain full message history.
const MAX_RECENT_FULL_CONVERSATIONS = 10;

const degradeConversation = conversation => {
  if (!conversation) return;
  const msgs = conversation.messages || [];
  if (msgs.length > 1) {
    const last = msgs[msgs.length - 1];
    conversation.messages = [last];
  }
  if (conversation.allMessagesLoaded) conversation.allMessagesLoaded = false;
  if (conversation.dataFetched) conversation.dataFetched = undefined;
};

const state = {
  allConversations: [],
  attachments: {},
  listLoadingStatus: true,
  chatStatusFilter: wootConstants.STATUS_TYPE.OPEN,
  chatSortFilter: wootConstants.SORT_BY_TYPE.LATEST,
  currentInbox: null,
  recentFullConversationIds: [],
  appliedFilters: [],
  contextMenuChatId: null,
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  conversationFilters: {},
  subscribedNonAssignedConversations: [],
  currentConversation: null,
  currentConversationId: null,
};

// mutations
export const mutations = {
  [types.SET_ALL_CONVERSATION](_state, conversationList) {
    const newAllConversations = [..._state.allConversations];
    conversationList.forEach(conversation => {
      const indexInCurrentList = newAllConversations.findIndex(
        c => c.id === conversation.id
      );
      if (indexInCurrentList < 0) {
        if (!_state.recentFullConversationIds.includes(conversation.id)) {
          degradeConversation(conversation);
        }
        newAllConversations.push(conversation);
      } else if (conversation.id !== _state.currentConversationId) {
        // If the conversation is already in the list, replace it
        // Added this to fix the issue of the conversation not being updated
        // When reconnecting to the websocket. If the currentConversationId is not the same as
        // the conversation.id in the store, replace the existing conversation with the new one
        if (!_state.recentFullConversationIds.includes(conversation.id)) {
          degradeConversation(conversation);
        }
        newAllConversations[indexInCurrentList] = conversation;
      } else {
        // If the conversation is already in the list and currentConversationId is the same,
        // replace all data except the messages array, attachments, dataFetched, allMessagesLoaded
        const existingConversation = newAllConversations[indexInCurrentList];
        newAllConversations[indexInCurrentList] = {
          ...conversation,
          allMessagesLoaded: existingConversation.allMessagesLoaded,
          messages: existingConversation.messages,
          dataFetched: existingConversation.dataFetched,
        };
      }
    });
    _state.allConversations = newAllConversations;
  },

  [types.REPLACE_ALL_CONVERSATIONS](_state, conversationList) {
    const newAllConversations = [];
    conversationList.forEach(conversation => {
      if (!_state.recentFullConversationIds.includes(conversation.id)) {
        degradeConversation(conversation);
      }
      newAllConversations.push(conversation);
    });

    const existingSelected = _state.allConversations.find(
      c => c.id === _state.currentConversationId
    );

    if (
      _state.currentConversationId &&
      existingSelected &&
      !newAllConversations.some(c => c.id === _state.currentConversationId)
    ) {
      degradeConversation(existingSelected);
      newAllConversations.push(existingSelected);
    }
    _state.allConversations = newAllConversations;
  },

  [types.EMPTY_ALL_CONVERSATION](_state) {
    _state.allConversations = [];
  },

  [types.SET_ALL_MESSAGES_LOADED](_state) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === _state.currentConversationId
    );
    if (chatOnList) TryVueSet(chatOnList, 'allMessagesLoaded', true);

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) TryVueSet(chatCurrent, 'allMessagesLoaded', true);
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === _state.currentConversationId
    );
    if (chatOnList) TryVueSet(chatOnList, 'allMessagesLoaded', false);

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) TryVueSet(chatCurrent, 'allMessagesLoaded', false);
  },

  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.currentConversationId = null;
    _state.currentConversation = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (data.length) {
      const [chatOnList] = _state.allConversations.filter(c => c.id === id);
      if (chatOnList) {
        const uniqueMessages = data.filter(
          message =>
            !chatOnList.messages.some(
              existingMessage => existingMessage.id === message.id
            )
        );
        chatOnList.messages.unshift(...uniqueMessages);
      }

      // remove from current conversation
      const chatCurrent =
        _state.currentConversationId === id
          ? _state.currentConversation
          : undefined;

      if (chatCurrent) {
        const uniqueMessages = data.filter(
          message =>
            !chatCurrent.messages.some(
              existingMessage => existingMessage.id === message.id
            )
        );
        chatCurrent.messages.unshift(...uniqueMessages);
      }
    }
  },

  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    const attachments = _state.attachments[id] || [];

    attachments.push(...data);
    _state.attachments[id] = [...attachments];
  },

  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    const [chatOnList] = _state.allConversations.filter(c => c.id === id);
    if (chatOnList) TryVueSet(chatOnList, 'messages', data);

    const chatCurrent =
      _state.currentConversationId === id
        ? _state.currentConversation
        : undefined;
    if (chatCurrent) TryVueSet(chatCurrent, 'messages', data);
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, conversation) {
    if (conversation) {
      // verify if conversation is already in the list
      const ids = _state.recentFullConversationIds.filter(
        id => id !== conversation.id
      );
      ids.push(conversation.id);

      // Degrade conversations that fall outside the limit.
      while (ids.length > MAX_RECENT_FULL_CONVERSATIONS) {
        const removedId = ids.shift();
        const toDegrade = _state.allConversations.find(c => c.id === removedId);
        degradeConversation(toDegrade);
      }

      // set top 10 conversation with messages to optimizes performance
      _state.recentFullConversationIds = ids;

      // set current conversation
      _state.currentConversationId = conversation.id;
      _state.currentConversation = conversation;
    }
  },

  [types.ASSIGN_AGENT](_state, assignee) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === _state.currentConversationId
    );
    if (chatOnList) TryVueSet(chatOnList.meta, 'assignee', assignee);

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) TryVueSet(chatCurrent.meta, 'assignee', assignee);
  },

  [types.ASSIGN_TEAM](_state, { team, conversationId }) {
    // chat into list
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === conversationId
    );
    if (chatOnList) TryVueSet(chatOnList.meta, 'team', team);

    // current chat
    const chatCurrent =
      _state.currentConversationId === conversationId
        ? _state.currentConversation
        : undefined;
    if (chatCurrent) TryVueSet(chatCurrent.meta, 'team', team);
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId }
  ) {
    // chat on list
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === conversationId
    );
    if (chatOnList) TryVueSet(chatOnList, 'last_activity_at', lastActivityAt);

    // current chat
    const chatCurrent =
      _state.currentConversationId === conversationId
        ? _state.currentConversation
        : undefined;
    if (chatCurrent) TryVueSet(chatCurrent, 'last_activity_at', lastActivityAt);
  },

  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === conversationId
    );
    if (chatOnList) TryVueSet(chatOnList, 'priority', priority);

    const chatCurrent =
      _state.currentConversationId === conversationId
        ? _state.currentConversation
        : undefined;
    if (chatCurrent) TryVueSet(chatCurrent, 'priority', priority);
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](_state, custom_attributes) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === _state.currentConversationId
    );
    if (chatOnList)
      TryVueSet(chatOnList, 'custom_attributes', custom_attributes);

    const chatCurrent = _state.currentConversation;
    if (chatCurrent)
      TryVueSet(chatCurrent, 'custom_attributes', custom_attributes);
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === Number(conversationId)
    );

    if (chatOnList) TryVueSet(chatOnList, 'snoozed_until', snoozedUntil);
    if (chatOnList) TryVueSet(chatOnList, 'status', status);

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) TryVueSet(chatCurrent, 'snoozed_until', snoozedUntil);
    if (chatCurrent) TryVueSet(chatCurrent, 'status', status);
  },

  [types.MUTE_CONVERSATION](_state) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === Number(_state.currentConversationId)
    );
    if (chatOnList) TryVueSet(chatOnList, 'muted', true);

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) TryVueSet(chatCurrent, 'muted', true);
  },

  [types.UNMUTE_CONVERSATION](_state) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === Number(_state.currentConversationId)
    );
    if (chatOnList) TryVueSet(chatOnList, 'muted', false);

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) TryVueSet(chatCurrent, 'muted', false);
  },

  [types.TOGGLE_EMAIL_CONTINUITY](_state) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === Number(_state.currentConversationId)
    );
    if (chatOnList) {
      TryVueSet(chatOnList, 'additional_attributes', {
        ...chatOnList.additional_attributes,
        force_email_channel: true,
      });
    }

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) {
      TryVueSet(chatCurrent, 'additional_attributes', {
        ...chatCurrent.additional_attributes,
        force_email_channel: true,
      });
    }
  },

  [types.ADD_CONVERSATION_ATTACHMENTS](_state, message) {
    // early return if the message has not been sent, or has no attachments
    if (
      message.status !== MESSAGE_STATUS.SENT ||
      !message.attachments?.length
    ) {
      return;
    }

    // Skip storing attachments for conversations outside recent full set.
    if (!_state.recentFullConversationIds.includes(message.conversation_id)) {
      return;
    }

    const id = message.conversation_id;
    const existingAttachments = _state.attachments[id] || [];

    const attachmentsToAdd = message.attachments.filter(attachment => {
      // if the attachment is not already in the store, add it
      // this is to prevent duplicates
      return !existingAttachments.some(
        existingAttachment => existingAttachment.id === attachment.id
      );
    });

    // replace the attachments in the store
    _state.attachments[id] = [...existingAttachments, ...attachmentsToAdd];
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS](_state, message) {
    if (message.status !== MESSAGE_STATUS.SENT) return;

    const { conversation_id: id } = message;
    const existingAttachments = _state.attachments[id] || [];
    if (!existingAttachments.length) return;

    _state.attachments[id] = existingAttachments.filter(attachment => {
      return attachment.message_id !== message.id;
    });
  },

  [types.ADD_MESSAGE](_state, message) {
    const { conversation_id: conversationId } = message;

    const [chatOnList] = _state.allConversations.filter(
      conversation => conversation.id === conversationId
    );

    const chatCurrent = _state.currentConversation;

    if (chatOnList) {
      const pendingMessageIndexOnList = findPendingMessageIndex(
        chatOnList,
        message
      );

      if (pendingMessageIndexOnList !== -1) {
        TryVueSet(chatOnList.messages, pendingMessageIndexOnList, message);
      } else {
        const isFull =
          _state.recentFullConversationIds.includes(conversationId);

        // Check if message already exists
        const messageExists = chatOnList.messages.some(
          m => m.id === message.id
        );

        if (!messageExists) {
          if (isFull) {
            chatOnList.messages.push(message);
          } else {
            chatOnList.messages = [message];
          }
          chatOnList.timestamp = message.created_at;
          const { conversation: { unread_count: unreadCount = 0 } = {} } =
            message;
          chatOnList.unread_count = unreadCount;
          if (_state.currentConversationId === conversationId) {
            emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
          }
        }
      }
    }

    // Only update the current conversation thread if the incoming message
    // belongs to the currently active conversation. Otherwise, ignore to
    // prevent cross-tab leakage.
    if (chatCurrent && _state.currentConversationId === conversationId) {
      const pendingMessageIndexCurrent = findPendingMessageIndex(
        chatCurrent,
        message
      );

      if (pendingMessageIndexCurrent !== -1) {
        TryVueSet(chatCurrent.messages, pendingMessageIndexCurrent, message);
      } else {
        const isFull =
          _state.recentFullConversationIds.includes(conversationId);

        // Check if message already exists
        const messageExists = chatCurrent.messages.some(
          m => m.id === message.id
        );

        if (!messageExists) {
          if (isFull) {
            chatCurrent.messages.push(message);
          } else {
            chatCurrent.messages = [message];
          }
          chatCurrent.timestamp = message.created_at;
          const { conversation: { unread_count: unreadCount = 0 } = {} } =
            message;
          chatCurrent.unread_count = unreadCount;
          emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
          emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        }
      }
    }
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    _state.allConversations.push(conversation);
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    const { allConversations } = _state;
    const currentConversationIndex = allConversations.findIndex(
      c => c.id === conversation.id
    );

    if (currentConversationIndex > -1) {
      const { messages, ...conversationAttributes } = conversation;
      const currentConversation = {
        ...allConversations[currentConversationIndex],
        ...conversationAttributes,
      };
      if (!_state.recentFullConversationIds.includes(conversation.id)) {
        degradeConversation(currentConversation);
      }
      TryVueSet(
        allConversations,
        currentConversationIndex,
        currentConversation
      );
      if (_state.currentConversationId === conversation.id) {
        _state.currentConversation = currentConversation;
        emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    } else {
      if (!_state.recentFullConversationIds.includes(conversation.id)) {
        degradeConversation(conversation);
      }

      _state.allConversations.push(conversation);
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    const [chatOnList] = _state.allConversations.filter(c => c.id === id);
    const chatCurrent =
      _state.currentConversationId === id
        ? _state.currentConversation
        : undefined;

    if (chatOnList) {
      TryVueSet(chatOnList, 'agent_last_seen_at', lastSeen);
      TryVueSet(chatOnList, 'unread_count', unreadCount);
    }

    if (chatCurrent) {
      TryVueSet(chatCurrent, 'agent_last_seen_at', lastSeen);
      TryVueSet(chatCurrent, 'unread_count', unreadCount);
    }
  },

  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  [types.UPDATE_ASSIGNEE](_state, payload) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === payload.id
    );
    if (chatOnList) TryVueSet(chatOnList.meta, 'assignee', payload.assignee);

    const chatCurrent =
      _state.currentConversationId === payload.id
        ? _state.currentConversation
        : undefined;

    if (chatCurrent) TryVueSet(chatCurrent.meta, 'assignee', payload.assignee);
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === conversationId
    );

    if (chatOnList) TryVueSet(chatOnList.meta, 'sender', payload);

    const chatCurrent =
      _state.currentConversationId === conversationId
        ? _state.currentConversation
        : undefined;

    if (chatCurrent) TryVueSet(chatCurrent.meta, 'sender', payload);
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === conversationId
    );

    if (chatOnList) TryVueSet(chatOnList, 'can_reply', canReply);

    const chatCurrent =
      _state.currentConversationId === conversationId
        ? _state.currentConversation
        : undefined;

    if (chatCurrent) TryVueSet(chatCurrent, 'can_reply', canReply);
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    // ??
    const chats = _state.allConversations.filter(
      c => c.meta.sender.id !== contactId
    );
    TryVueSet(_state, 'allConversations', chats);
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { conversationId, messageId }
  ) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },

  [types.SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },

  [types.UPDATE_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },

  [types.HANDLE_SUBSCRIBE_NON_ASSIGNED_CONVERSATION](_state, conversationId) {
    const newList = [..._state.subscribedNonAssignedConversations];

    const existingIndex = newList.findIndex(item => item === conversationId);
    if (existingIndex !== -1) {
      newList.splice(existingIndex, 1);
    }

    newList.push(conversationId);

    if (newList.length > MAX_SUBSCRIBED_CONVERSATIONS) {
      newList.splice(0, newList.length - MAX_SUBSCRIBED_CONVERSATIONS);
    }

    _state.subscribedNonAssignedConversations = newList;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
